import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ACondition, AObject, CacheService, Operator, PlatformService } from '@congacommerce/core';
import { CartService, LineStatus } from '@congacommerce/ecommerce';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, retry, share, take, tap } from 'rxjs/operators';
import {
    RetrieveClienteBroadBandOrderLineItemReq,
    RetrieveClienteBroadBandProposalLineItemReq,
    RetrieveClienteCommCompletedQuoteWithoutOrderReq,
    RetrieveClienteCommOrderLineItemReq,
    RetrieveClienteCommProposalLineItemReq,
    RetrieveVendAbbinataOrderLineItemReq,
    RetrieveVendAbbinataProposalLineItemReq
} from '../../../modules/common/order-entry/models/apttus-query-request';
import {
    RetrieveClienteBroadBandOrderLineItem,
    RetrieveClienteBroadBandProposalLineItem,
    RetrieveClienteCommCompletedQuoteWithoutOrder,
    RetrieveClienteCommOrderLineItem,
    RetrieveClienteCommProposalLineItem,
    RetrieveVendAbbinataOrderLineItem,
    RetrieveVendAbbinataProposalLineItem
} from '../../../modules/common/order-entry/models/apttus-query-response';
import { OrderEntryProvider } from '../../../modules/common/order-entry/providers/order-entry-provider';
import { resetOrderEntry, setPlicoUrl } from '../../../store/actions/order-entry.actions';
import { resetUserState, setCartSegment } from '../../../store/actions/user.actions';
import { FlowType } from '../../../store/models/flow-type';
import { EglState } from '../../../store/reducers';
import { selectFlowType, selectSalesProcess } from '../../../store/selectors/order-entry.selectors';
import { selectAgentInfo, selectCurrentVirtualAgent } from '../../../store/selectors/user.selectors';
import { DOCUMENT_TYPE_CONFIG, SKIP_DOCUMENT_GENERATION } from '../../config/document-ot.config';
import { RoutesPaths } from '../../config/routes-paths';
import { AtpCustomerCategory } from '../../enums/apttus/apt-customer-category';
import { AptCustomerType } from '../../enums/apttus/apt-customer-type';
import { AptLineType } from '../../enums/apttus/apt-line-type';
import { AptProductFamily } from '../../enums/apttus/apt-product-family';
import { AptQuoteStatus } from '../../enums/apttus/apt-quote-status';
import { AptSalesProcess } from '../../enums/apttus/apt-sales-process';
import { AptSignatureMode } from '../../enums/apttus/apt-signature-mode';
import { ApimApttusQueryId } from '../../enums/shared/apim-apttus-query-id';
import { ClearStateFlags } from '../../enums/shared/clear-state-flags';
import { cleanObj, EnumContainsFlag } from '../../functions/misc.functions';
import { ifOp, retryWithDelay } from '../../functions/observable-operators';
import { convertSegmentAptToD365, convertSegmentD365toApt } from '../../functions/remap.functions';
import { flowTypeUtil } from '../../functions/verifications.functions';
import { StatusResponse } from '../../interfaces/base-api-response';
import { SalesforceQueryResult } from '../../models/apttus/salesforce-query-result';
import { EglCartExtended } from '../../models/apttus/tables/cart/egl-cart-extended';
import { EglQuotePAVLight } from '../../models/apttus/tables/pav/egl-quote-p-a-v-light';
import { EglQuoteLight } from '../../models/apttus/tables/quote/egl-quote-light';
import { EglAssetLineItem } from '../../models/apttus/tables/user/egl-asset-line-item';
import { AgentInfo, VirtualAgent } from '../../models/user/agent';
import { CommonProvider } from '../../providers/common-provider';
import { TelemetryMetricService } from '../app/telemetry-metric.service';
import { LoadingService } from '../shared/loading.service';
import { LoggerService } from '../shared/logger.service';
import { EglAssetLineItemService } from './tables/asset/egl-assetline-item.service';
import { EglPAVLightService } from './tables/egl-pav-light.service';
import { EglQuoteLightService } from './tables/quote/egl-quote-light.service';

@Injectable({
    providedIn: 'root'
})
export class ApttusService {
    public showDropCartModal = new EventEmitter();
    public onCreateNewCartCompleted = new EventEmitter<string>();
    private _cartId: string;

    private itemRemovalInProgress = new BehaviorSubject<boolean>(false);
    currentSubject = this.itemRemovalInProgress.asObservable();

    private configRulesLoaded = new BehaviorSubject<boolean>(false);
    configRulesSubject$ = this.configRulesLoaded.asObservable();
    flowType: FlowType;

    constructor(
        private cartSrv: CartService,
        private router: Router,
        private store: Store<EglState>,
        private loggerSrv: LoggerService,
        private eglPAVLightSrv: EglPAVLightService,
        private pltSrv: PlatformService,
        private cacheSrv: CacheService,
        private eglQuoteLightSrv: EglQuoteLightService,
        private commonPrv: CommonProvider,
        private telemetrySrv: TelemetryMetricService,
        private translateSrv: TranslateService,
        private provider: OrderEntryProvider,
        private eglAssetlineItemServ: EglAssetLineItemService
    ) {}

    /**
     * Aggiornamento di tutti di un blocco di PAV in input
     * @param pavs: PAV da aggiornare
     */
    updatePavs(pavs: EglQuotePAVLight[]): Observable<boolean> {
        const loaderFinalizer = LoadingService.loaderOperator();
        const telemetryFinalizer = this.telemetrySrv.rxTelemetry('Update PAV');
        return this.eglPAVLightSrv.update(<AObject[]>pavs).pipe(
            take(1),
            telemetryFinalizer,
            loaderFinalizer,
            tap((res: (EglQuotePAVLight & AObject)[]) => {
                this.cartSrv.refreshCart();
                if (!res?.length) {
                    throw new Error('Errore aggiornamento PAV');
                }
                this.loggerSrv.info('PAV aggiornati con successo');
            }),
            map(() => true),
            catchError((error) => {
                this.loggerSrv.error(null, error?.message, null, true);
                return of(false);
            })
        );
    }

    /**
     * Metodo temporaneo perchè deprecato
     * @param navigateTo
     * @param customerType
     */
    clearAndCreateNewCart$(navigateTo: any, customerType?: AptCustomerType): Observable<any> {
        const telemetryFinalizer = this.telemetrySrv.rxTelemetry('Empty Cart');
        return this.cartSrv.getMyCart().pipe(
            take(1),
            map((cart) => cart as EglCartExtended),
            tap((currentCart) => this.cartSrv.deleteCart(currentCart)),
            mergeMap((currentCart) =>
                this.createNewCart(
                    new EglCartExtended(),
                    customerType || currentCart?.egl_customer_type,
                    navigateTo
                ).pipe(share(), retry(3))
            ),
            telemetryFinalizer,
            LoadingService.loaderOperator('Svuoto il carrello')
        );
    }

    /**
     * Cancella il carrello attualmente in uso e ne crea uno nuovo
     * @param navigateTo: indica se dopo l'operazione bisogna fare redirect
     * @deprecated *** USARE CARTUTILITYSERVICE PER LA CREAZIONE DI NUOVO CARRELLO. TODO RIMUOVERE TUTTE LE CHIAMATE A QUESTO METODO ***
     */
    clearAndCreateNewCart(navigateTo: any, customerType?: AptCustomerType): void {
        this.clearAndCreateNewCart$(navigateTo, customerType).subscribe(
            () => this.clearState(),
            (err) => this.loggerSrv.error(null, err?.message, true, true)
        );
    }

    /**
     * @description: Crea un nuovo carrello partendo da un oggetto in input
     * @param cart: istanza carrello da creare
     * @param customerType: segmento carrello
     * @param navigateTo: se dopo la creazione deve essere effettuata una navigazione
     * @param reloadApp: se dopo la creazione deve essere ricaricata la pagina
     * @param clearState: se voglio resettare lo state Redux
     * @param showLoading: se dopo vuoi mostrare il messaggio 'Creazione nuovo carrello' durante l'elaborazione della richiesta
     * @deprecated *** USARE CARTUTILITYSERVICE PER LA CREAZIONE DI NUOVO CARRELLO. TODO RIMUOVERE TUTTE LE CHIAMATE A QUESTO METODO ***
     */
    createNewCart(
        cart: EglCartExtended = new EglCartExtended(),
        customerType?: AptCustomerType,
        navigateTo: any = null,
        reloadApp: boolean = false,
        clearState: boolean = false,
        showLoading: boolean = true,
        salesProcessCart?: string
    ): Observable<EglCartExtended> {
        this.loggerSrv.info('Creating new cart...');

        return combineLatest([
            this.store.select(selectCurrentVirtualAgent),
            this.store.select(selectAgentInfo),
            this.store.select(selectSalesProcess)
        ]).pipe(
            take(1),
            filter(([virtualAgent, agentInfo]) => !!virtualAgent && !!agentInfo),
            map(
                ([{ VirtualAgency }, { Agent, UserConfiguration }, salesProcess]: [
                    VirtualAgent,
                    AgentInfo,
                    AptSalesProcess
                ]) => ({
                    virtualAgency: VirtualAgency,
                    agent: Agent,
                    salesProcess,
                    customerType: customerType || convertSegmentD365toApt(UserConfiguration?.LastUsedCustomerSegment)
                })
            ),
            map(
                ({ virtualAgency, agent, salesProcess, customerType }): EglCartExtended =>
                    Object.assign(cart, {
                        egl_agency_code: virtualAgency?.Code,
                        egl_sales_channel: virtualAgency?.Channel?.Code,
                        egl_sales_process: salesProcessCart ? salesProcessCart : salesProcess,
                        egl_DAG_code: agent?.Code,
                        egl_customer_type: customerType
                    })
            ),
            mergeMap((data) =>
                reloadApp || navigateTo
                    ? this.router.navigate(reloadApp ? [RoutesPaths.Dashboard] : navigateTo).then(() => data)
                    : of(data)
            ),
            mergeMap((newCart) => this.cartSrv.createNewCart(newCart).pipe(take(1))),
            this.telemetrySrv.rxTelemetry('create-new-cart'),
            tap((newCart) => this.loggerSrv.info(`Activating cart id ${newCart?.Id}...`)),
            tap(() => this.cartSrv.refreshCart()),
            tap((activatedCart) => {
                this.loggerSrv.info(`Cart ${activatedCart?.Id} activated`);
                if (customerType) {
                    this.store.dispatch(setCartSegment({ payload: convertSegmentAptToD365(customerType) }));
                }
            }),
            tap(() => clearState && this.clearState()),
            tap((activatedCart) => {
                this.onCreateNewCartCompleted.emit(activatedCart?.Id);
            }),
            ifOp(showLoading, LoadingService.loaderOperator('Creazione nuovo carrello')),
            tap(() => reloadApp && window.location.reload())
        ) as Observable<EglCartExtended>;
    }

    /* getter */

    /**
     * @deprecated NON USARE. Basarsi su valore presente nel local storage
     * @param cartId
     */
    setCartId(cartId: string): void {
        this._cartId = cartId;
    }

    /**
     * @deprecated NON USARE. Se proprio hai disogno di un cartId diverso da quello presente nel localStorage usa lo state. OrderEntry.cartId
     */
    get cartId(): string {
        return this._cartId || CartService.getCurrentCartId();
    }

    isItemRemovalInProgress(input: boolean): void {
        this.itemRemovalInProgress.next(input);
    }

    areConfigRulesLoaded(input: boolean): void {
        this.configRulesLoaded.next(input);
    }

    public clearState(clearCache = false, clearStateFlags = ClearStateFlags.OrderEntry | ClearStateFlags.User): void {
        if (clearCache) {
            this.cacheSrv
                .clear()
                .pipe(take(1))
                .subscribe(() => this.loggerSrv.info('Cache cleaned'));
        }

        if (EnumContainsFlag(clearStateFlags, ClearStateFlags.OrderEntry)) {
            this.store.dispatch(resetOrderEntry());
        }

        if (EnumContainsFlag(clearStateFlags, ClearStateFlags.User)) {
            this.store.dispatch(resetUserState());
        }
    }

    finalizeFlow$<R extends EglQuoteLight>(quote: R): Observable<R> {
        this.store.select(selectFlowType).subscribe((flowType) => (this.flowType = flowType));
        const isFlowTypeVocal =
            flowTypeUtil(this.flowType).equalTo(
                FlowType.VariazioneTecnicaLavoriPreventivoAumentoPotenza,
                FlowType.Cessazione
            ) && quote.egl_signature_mode === AptSignatureMode.VocalOrder;

        return (
            SKIP_DOCUMENT_GENERATION.includes(quote.egl_signature_mode) || isFlowTypeVocal
                ? of(null).pipe(
                      tap(() => {
                          this.loggerSrv.warn(
                              `Skip quote document generation. Quote signature_mode was: ${quote.egl_signature_mode}`,
                              false
                          );
                      })
                  )
                : this.recursiveCallToOpenText$(quote.Id)
        ).pipe(
            mergeMap(() => this.unlockFlow$(quote)),
            LoadingService.loaderOperator('Finalizzazione offerta'),
            catchError((err) => {
                this.loggerSrv.error(null, 'finalizeFlow_obs have an error', err, false);
                throw err;
            })
        );
    }

    /**
     * @description: Attiva lo state model lato Apttus
     * @param quote: [quote] creata
     * @return: void
     */
    private unlockFlow$<R extends EglQuoteLight>(quote: R): Observable<R> {
        if (!quote) return of(null);

        this.loggerSrv.info('try to unlock flow...');
        quote.egl_flowunlock = true;
        return this.eglQuoteLightSrv.update([quote], false).pipe(
            map((res) => ((res || [])[0] || quote) as R),
            tap((quote) => this.loggerSrv.info('egl_flowunlock:', quote.egl_flowunlock)),
            catchError((err) => {
                this.loggerSrv.error(null, 'Error update quote on unlockFlow_obs', err, false);
                throw new Error('Error update quote on unlockFlow_obs');
            }),
            this.telemetrySrv.rxTelemetry('update-quote_egl_flowunlock')
        );
    }

    /**
     * @description: Metodo ricorsivo generazione plico. A causa del set 'async' lato Apttus
     * le informazioni della quote potrebbero non essere disponibili al momento
     * della richiesta di generazione, causando eccezione. Questo metodo ritenta
     * ricorsivamente, per un max di 15 volte ogni 3 sec, di generare il plico
     */
    recursiveCallToOpenText$(quoteId: string, maxAttempts: number = 15): Observable<string> {
        return this.store.select(selectFlowType).pipe(
            take(1),
            map((flowType) => ({
                quoteId,
                documentType: DOCUMENT_TYPE_CONFIG[flowType] || DOCUMENT_TYPE_CONFIG.DEFAULT
            })),
            mergeMap(({ quoteId, documentType }) =>
                this.commonPrv.generateDocumentOT_obs(quoteId, documentType).pipe(
                    map((res) => {
                        this.loggerSrv.info(`quote documentType: ${documentType}`);
                        if (res?.status === StatusResponse.Success && res?.response?.fileUrl) {
                            return res?.response?.fileUrl;
                        }
                        throw new Error(`quote document not generated: ${documentType}`);
                    }),
                    tap((fileUrl) => {
                        this.loggerSrv.info(`document URL`, fileUrl);
                        this.store.dispatch(setPlicoUrl({ payload: fileUrl }));
                    }),
                    retryWithDelay(maxAttempts, 3000, `Errore generazione documento: ${documentType}`),
                    LoadingService.loaderOperator('Generazione documentazione in corso'),
                    this.telemetrySrv.rxTelemetry('plico-generated')
                )
            )
        );
    }

    cartHaveQuote(cartId: string = CartService.getCurrentCartId()): Observable<{ quoteId: string; cartId: string }> {
        if (!cartId) return of(null);
        return this.commonPrv
            .query<
                SalesforceQueryResult<{
                    id: string;
                    Apttus_QPConfig__Proposald__c: string;
                }>
            >(
                `SELECT Id, Apttus_QPConfig__Proposald__c from Apttus_Config2__ProductConfiguration__c where id = '${cartId}'`
            )
            .pipe(
                map(({ records }) =>
                    records[0]?.Apttus_QPConfig__Proposald__c
                        ? {
                              quoteId: records[0].Apttus_QPConfig__Proposald__c,
                              cartId
                          }
                        : null
                )
            );
    }

    getEffectiveDate(orderId: string): Observable<{
        id: string;
        egl_Effective_date__c: string;
    }> {
        if (!orderId) return of(null);

        const query = `select Id, egl_Effective_date__c from Apttus_Config2__Order__c where Id = '${orderId}'`;
        const obs = this.commonPrv.query<
            SalesforceQueryResult<{
                id: string;
                egl_Effective_date__c: string;
            }>
        >(query.replace(' ', '+'));
        return obs.pipe(
            filter((res) => !!res && res.totalSize === 1),
            map((res) => res?.records[0])
        );
    }

    getActiveCommodity(taxCode: string): Observable<number> {
        /* 
            `SELECT id 
            FROM Apttus_Config2__AssetLineItem__c 
            WHERE Apttus_Config2__LineType__c='Product/Service' 
                AND Apttus_Config2__IsPrimaryLine__c=true 
                AND Apttus_Config2__OptionId__c = null
                AND Apttus_Config2__ProductId__r.Apttus_Config2__ProductType__c in ('Prodotto Commodity Luce','Prodotto Commodity Gas')
                AND Apttus_Config2__AccountId__r.egl_customer_fiscalcode__c = '${taxCode}'
                AND Apttus_Config2__AssetStatus__c = 'Activated'`
        */
        return this.eglAssetlineItemServ
            .where([
                new ACondition(EglAssetLineItem, 'LineType', Operator.EQUAL, 'Product/Service'),
                new ACondition(EglAssetLineItem, 'Apttus_Config2__IsPrimaryLine__c', Operator.EQUAL, true),
                new ACondition(EglAssetLineItem, 'Apttus_Config2__OptionId__c', Operator.EQUAL, null),
                new ACondition(EglAssetLineItem, 'Product.ProductType', Operator.IN, [
                    'Prodotto Commodity Luce',
                    'Prodotto Commodity Gas'
                ]),
                new ACondition(
                    EglAssetLineItem,
                    'Apttus_Config2__AccountId.egl_customer_fiscalcode',
                    Operator.EQUAL,
                    taxCode
                ),
                new ACondition(EglAssetLineItem, 'Apttus_Config2__AssetStatus__c', Operator.EQUAL, 'Activated')
            ])
            .pipe(
                map((assetLineItemList: EglAssetLineItem[]) => assetLineItemList.map(({ Id }) => Id)),
                tap((assetIds: string[]) =>
                    this.loggerSrv.info('getActiveCommodity: ', { taxCode: taxCode, assetIds: assetIds })
                ),
                map((assetIds: string[]) => assetIds.length)
            );
    }

    getPendingCommodityOrders(taxCode: string): Observable<number> {
        /*
            SELECT id 
            FROM Apttus_Config2__OrderLineItem__c 
            WHERE Apttus_Config2__Status__c NOT IN ('Activated','Fulfilled','Superseded','Cancelled')
                AND Apttus_Config2__ProductId__r.Apttus_Config2__ProductType__c  IN ('Prodotto Commodity Luce','Prodotto Commodity Gas')
                AND Apttus_Config2__OrderId__r.Apttus_Config2__SoldToAccountId__r.egl_customer_fiscalcode__c = '${taxCode}'
                AND Apttus_Config2__LineType__c = 'Product/Service' 
                AND Apttus_Config2__OrderId__r.egl_macro_sales_process__c in ('VOLTURA CON SWITCH','SWITCH IN','ATTIVAZIONE','CAMBIO PRODOTTO','VOLTURA')`
        */
        return this.provider
            .apttusQuery<RetrieveClienteCommOrderLineItem>(
                ApimApttusQueryId.RetrieveClienteCommOrderLineItem,
                cleanObj({
                    egl_customer_fiscalcode__c: taxCode
                } as RetrieveClienteCommOrderLineItemReq)
            )
            .pipe(
                map((res) => res?.TotalSize),
                catchError((err: Error) => {
                    this.loggerSrv.error(null, err.message, err, true);
                    throw Error;
                }),
                LoadingService.loaderOperator()
            );
    }

    getPendingCommodityQuotes(taxCode: string): Observable<number> {
        /*
            `SELECT id 
            FROM Apttus_Proposal__Proposal_Line_Item__c
            WHERE Apttus_Proposal__Proposal__r.Apttus_Proposal__Account__r.egl_customer_fiscalcode__c = '${taxCode}'
                AND 
                    (
                        (
                            Apttus_Proposal__Proposal__r.egl_quote_status__c NOT IN ('COMPLETATO','RIFIUTATO','ANNULLATO','CANCELLATO')
                            AND Apttus_Proposal__Proposal__r.egl_quote_status__c != ''
                        )
                        OR
                        (
                            Apttus_Proposal__Proposal__r.Apttus_Proposal__Approval_Stage__c != 'Accepted'
                            AND Apttus_Proposal__Proposal__r.egl_quote_status__c = ''
                        )
                    )
                AND Apttus_QPConfig__LineType__c = 'Product/Service' 
                AND Apttus_QPConfig__IsPrimaryLine__c = true
                AND Apttus_Proposal__Product__r.Apttus_Config2__ProductType__c  IN ('Prodotto Commodity Luce','Prodotto Commodity Gas')
                AND Apttus_Proposal__Proposal__r.egl_macro_sales_process__c in ('VOLTURA CON SWITCH','SWITCH IN','ATTIVAZIONE','CAMBIO PRODOTTO','VOLTURA') `
        */
        return this.provider
            .apttusQuery<RetrieveClienteCommProposalLineItem>(
                ApimApttusQueryId.RetrieveClienteCommProposalLineItem,
                cleanObj({
                    egl_customer_fiscalcode__c: taxCode
                } as RetrieveClienteCommProposalLineItemReq)
            )
            .pipe(
                map((res) => res?.TotalSize),
                catchError((err: Error) => {
                    this.loggerSrv.error(null, err.message, err, true);
                    throw Error;
                }),
                LoadingService.loaderOperator()
            );
    }

    getCompletedCommodityQuotesWithoutOrder(taxCode: string): Observable<number> {
        /*
            SELECT id
                FROM Apttus_Proposal__Proposal_Line_Item__c
                WHERE Apttus_Proposal__Proposal__r.Apttus_Proposal__Account__r.egl_customer_fiscalcode__c = '{{egl_customer_fiscalcode__c}}'
                    AND Apttus_Proposal__Proposal__r.egl_quote_status__c = 'COMPLETATO'
                    AND Apttus_QPConfig__LineType__c = 'Product/Service'
                    AND Apttus_QPConfig__IsPrimaryLine__c = true
                    AND Apttus_Proposal__Product__r.Apttus_Config2__ProductType__c IN ('Prodotto Commodity Luce','Prodotto Commodity Gas')
                    AND Apttus_Proposal__Proposal__r.egl_macro_sales_process__c in ('VOLTURA CON SWITCH','SWITCH IN','ATTIVAZIONE','CAMBIO PRODOTTO','VOLTURA')
                    AND Apttus_Proposal__Proposal__c NOT IN (
                        SELECT Apttus_QPConfig__ProposalId__c FROM Apttus_Config2__OrderLineItem__c WHERE Apttus_QPConfig__ProposalId__r.Apttus_Proposal__Account__r.egl_customer_fiscalcode__c = '{{egl_customer_fiscalcode__c}}'
                    )
        */
        return this.provider
            .apttusQuery<RetrieveClienteCommCompletedQuoteWithoutOrder>(
                ApimApttusQueryId.RetrieveClienteCommCompletedQuoteWithoutOrder,
                cleanObj({
                    egl_customer_fiscalcode__c: taxCode
                } as RetrieveClienteCommCompletedQuoteWithoutOrderReq)
            )
            .pipe(
                map((res) => res?.TotalSize),
                catchError((err: Error) => {
                    this.loggerSrv.error(null, err.message, err, true);
                    throw Error;
                }),
                LoadingService.loaderOperator()
            );
    }

    getPendingBroadbandOrders(taxCode: string): Observable<number> {
        /*
            `SELECT id 
            FROM Apttus_Config2__OrderLineItem__c 
            WHERE Apttus_Config2__Status__c NOT IN ('Activated','Fulfilled','Superseded','Cancelled')
                AND Apttus_Config2__OrderId__r.Apttus_Config2__SoldToAccountId__r.egl_customer_fiscalcode__c = '${taxCode}'
                AND Apttus_Config2__LineType__c = 'Product/Service' 
                AND Apttus_Config2__OrderId__r.egl_macro_sales_process__c = 'BROADBAND VENDITA' `
        */

        return this.provider
            .apttusQuery<RetrieveClienteBroadBandOrderLineItem>(
                ApimApttusQueryId.RetrieveClienteBroadBandOrderLineItem,
                cleanObj({
                    taxCode
                } as RetrieveClienteBroadBandOrderLineItemReq)
            )
            .pipe(
                map((res) => res?.TotalSize),
                catchError((err: Error) => {
                    this.loggerSrv.error(null, err.message, err, true);
                    throw Error;
                }),
                LoadingService.loaderOperator()
            );
    }

    getPendingBroadbandQuotes(taxCode: string): Observable<number> {
        /* 
            `SELECT id
            FROM Apttus_Proposal__Proposal_Line_Item__c
            WHERE Apttus_Proposal__Proposal__r.Apttus_Proposal__Account__r.egl_customer_fiscalcode__c = '${taxCode}'
                AND Apttus_QPConfig__LineType__c = 'Product/Service' 
                AND Apttus_QPConfig__IsPrimaryLine__c = true
                AND Apttus_Proposal__Proposal__r.egl_macro_sales_process__c = 'BROADBAND VENDITA'`
                AND 
                    (
                        (
                            Apttus_Proposal__Proposal__r.egl_quote_status__c NOT IN ('COMPLETATO','RIFIUTATO','ANNULLATO','CANCELLATO')
                            AND Apttus_Proposal__Proposal__r.egl_quote_status__c != ''
                        )
                        OR
                        (
                            Apttus_Proposal__Proposal__r.Apttus_Proposal__Approval_Stage__c != 'Accepted'
                            AND Apttus_Proposal__Proposal__r.egl_quote_status__c = ''
                        )
                    )
        */
        return this.provider
            .apttusQuery<RetrieveClienteBroadBandProposalLineItem>(
                ApimApttusQueryId.RetrieveClienteBroadBandProposalLineItem,
                cleanObj({
                    taxCode
                } as RetrieveClienteBroadBandProposalLineItemReq)
            )
            .pipe(
                map((res) => res?.TotalSize),
                catchError((err: Error) => {
                    this.loggerSrv.error(null, err.message, err, true);
                    throw Error;
                }),
                LoadingService.loaderOperator()
            );
    }

    getActiveBroadband(taxCode: string): Observable<number> {
        /* 
            `SELECT id 
            FROM Apttus_Config2__AssetLineItem__c 
            WHERE Apttus_Config2__LineType__c='Product/Service'
                AND Apttus_Config2__IsPrimaryLine__c=true
                AND Apttus_Config2__OptionId__c = null
                AND Apttus_Config2__ProductId__r.Family = 'BroadBand' 
                AND Apttus_Config2__AccountId__r.egl_customer_fiscalcode__c = '${taxCode}'
                AND Apttus_Config2__AssetStatus__c = 'Activated'`
        */
        return this.eglAssetlineItemServ
            .where([
                new ACondition(EglAssetLineItem, 'LineType', Operator.EQUAL, AptLineType.ProductService),
                new ACondition(EglAssetLineItem, 'IsPrimaryLine', Operator.EQUAL, true),
                new ACondition(EglAssetLineItem, 'Apttus_Config2__OptionId', Operator.EQUAL, null),
                new ACondition(EglAssetLineItem, 'Product.Family', Operator.EQUAL, AptProductFamily.BroadBand),
                new ACondition(
                    EglAssetLineItem,
                    'Apttus_Config2__AccountId.egl_customer_fiscalcode',
                    Operator.EQUAL,
                    taxCode
                ),
                new ACondition(EglAssetLineItem, 'AssetStatus', Operator.EQUAL, LineStatus.Activate)
            ])
            .pipe(
                map((assetLineItemList: EglAssetLineItem[]) => assetLineItemList.map(({ Id }) => Id)),
                tap((assetIds: string[]) =>
                    this.loggerSrv.info('getActiveBroadband: ', { taxCode: taxCode, assetIds: assetIds })
                ),
                map((assetIds: string[]) => assetIds.length)
            );
    }

    private logCategoryAction(categoryCustomer: string, resultQuery: number): boolean {
        if (categoryCustomer === AtpCustomerCategory.Commodity && resultQuery === 0) {
            this.loggerSrv.error(
                'Attenzione!',
                this.translateSrv.instant('ORDER_ENTRY.ACTIVE_SUPPLIES.CB_COMMODITY_ERROR'),
                null,
                true
            );
            return false;
        } else if (categoryCustomer === AtpCustomerCategory.Base && resultQuery > 0) {
            this.loggerSrv.error(
                'Attenzione!',
                this.translateSrv.instant('ORDER_ENTRY.ACTIVE_SUPPLIES.MSG_ERROR_CATEGORY_CHANGE'),
                null,
                true
            );
            return false;
        }
        return true;
    }

    /**
     * Verifica se un cliente ha record attivi in base alla categoria e al codice fiscale.
     * @param categoryCustomers - Array di categorie del cliente (e.g., Commodity, Base).
     * @param taxCode - Codice fiscale del cliente.
     * @returns Observable<boolean> - True se non ci sono record attivi, false se ci sono record attivi e categoryCustomer è Commodity (messaggio bloccante) o se categoryCustomer è Base ma ha forniture commodity attive (richiesta cambio categoria).
     */
    customerBaseQueries(categoryCustomers: string[], taxCode: string): Observable<boolean> {
        return combineLatest([
            this.getActiveCommodity(taxCode),
            this.getPendingCommodityOrders(taxCode),
            this.getPendingCommodityQuotes(taxCode),
            this.getCompletedCommodityQuotesWithoutOrder(taxCode)
        ]).pipe(
            take(1),
            map((res) => {
                const resultQuery = res.reduce((acc, curr) => acc + curr, 0);

                return categoryCustomers.every((categoryCustomer) =>
                    this.logCategoryAction(categoryCustomer, resultQuery)
                );
            })
        );
    }

    getDataFromContractCode(relatedContractCode: string): Observable<{
        taxCode: string;
        quoteStatus: AptQuoteStatus;
        quoteId: string;
    }> {
        /*
            `SELECT Apttus_Proposal__Proposal__c
                , Apttus_Proposal__Proposal__r.egl_quote_status__c
                , Apttus_Proposal__Proposal__r.Apttus_Proposal__Account__r.egl_customer_fiscalcode__c 
            from Apttus_Proposal__Proposal_Line_Item__c 
            WHERE Apttus_Proposal__Proposal__r.egl_contract_code__c = '${relatedContractCode}' 
                AND Apttus_Proposal__Product__r.Apttus_Config2__ProductType__c in ('prodotto commodity luce','prodotto commodity gas')`
        */
        return this.provider
            .apttusQuery<RetrieveVendAbbinataProposalLineItem>(
                ApimApttusQueryId.RetrieveVendAbbinataProposalLineItem,
                cleanObj({
                    egl_contract_code__c: relatedContractCode
                } as RetrieveVendAbbinataProposalLineItemReq)
            )
            .pipe(
                map(
                    (res) =>
                        res?.Records?.flatMap((r) => ({
                            taxCode:
                                r?.Apttus_Proposal__Proposal__r?.Apttus_Proposal__Account__r
                                    ?.egl_customer_fiscalcode__c,
                            quoteStatus: r?.Apttus_Proposal__Proposal__r?.egl_quote_status__c,
                            quoteId: r?.Apttus_Proposal__Proposal__c
                        }))[0]
                ),
                catchError((err: Error) => {
                    this.loggerSrv.error(null, err.message, err, true);
                    throw Error;
                }),
                LoadingService.loaderOperator()
            );
    }

    quoteWithLinkedAsset(quoteId: string): Observable<boolean> {
        /*
            `SELECT id
                , Apttus_Config2__AssetLineItemId__c
                , Apttus_QPConfig__ProposalId__r.egl_quote_status__c
                , Apttus_QPConfig__ProposalId__r.Apttus_Proposal__Account__r.egl_customer_fiscalcode__c 
            FROM Apttus_Config2__OrderLineItem__c 
            WHERE Apttus_QPConfig__ProposalId__c = '${quoteId}' 
                AND Apttus_Config2__Status__c in ('Activated', 'Fulfilled') 
                AND Apttus_Config2__AssetLineItemId__c  != null`;
        */
        return this.provider
            .apttusQuery<RetrieveVendAbbinataOrderLineItem>(
                ApimApttusQueryId.RetrieveVendAbbinataOrderLineItem,
                cleanObj({
                    Apttus_QPConfig__ProposalId__c: quoteId
                } as RetrieveVendAbbinataOrderLineItemReq)
            )
            .pipe(
                map((res) => res?.TotalSize > 0),
                catchError((err: Error) => {
                    this.loggerSrv.error(null, err.message, err, true);
                    throw Error;
                }),
                LoadingService.loaderOperator()
            );
    }

    /**
     * Verifica se un cliente ha inserito un codice plico in configurazione per il prodotto BB e se corrisponde al CF inserito in OE.
     * @param taxCode - Codice fiscale del cliente.
     * @param relatedContractCode - Codice plico associato in fase di configurazione prodotto.
     * @returns Observable<boolean> - True se c'è un match tra il cf e il codice.
     */
    taxCodePlicoQueries(taxCode: string, relatedContractCode: string): Observable<boolean> {
        if (relatedContractCode) {
            return this.getDataFromContractCode(relatedContractCode).pipe(
                take(1),
                concatMap((dataFromContractCode) => {
                    if (!dataFromContractCode?.quoteId) {
                        this.loggerSrv.error(
                            'Attenzione!',
                            this.translateSrv.instant('ORDER_ENTRY.ACTIVE_SUPPLIES.MSG_RELATED_CONTRACT_CODE'),
                            null,
                            true
                        );
                        return of(false);
                    }
                    return this.quoteWithLinkedAsset(dataFromContractCode?.quoteId).pipe(
                        take(1),
                        map((hasLinkedAsset) => {
                            const isConsistent =
                                dataFromContractCode?.taxCode === taxCode &&
                                dataFromContractCode?.quoteStatus !== AptQuoteStatus.Cancellato &&
                                dataFromContractCode?.quoteStatus !== AptQuoteStatus.Rifiutato &&
                                !hasLinkedAsset;
                            if (isConsistent) {
                                return true;
                            } else {
                                this.loggerSrv.error(
                                    'Attenzione!',
                                    this.translateSrv.instant('ORDER_ENTRY.ACTIVE_SUPPLIES.MSG_RELATED_CONTRACT_CODE'),
                                    null,
                                    true
                                );
                                return false;
                            }
                        })
                    );
                })
            );
        }
        return of(true);
    }

    /**
     * Verifica se il CF ha Offerte/Ordini BroadBand pending oppure Asset Broadband attivi mostrando un messaggio di warning.
     * @param taxCode - Codice fiscale del cliente.
     * @returns Observable<boolean> - True se nonerifica False atr.
     **/
    taxCodeQuoteOrderAssetBB(taxCode: string): Observable<boolean> {
        return combineLatest([
            this.getActiveBroadband(taxCode),
            this.getPendingBroadbandOrders(taxCode),
            this.getPendingBroadbandQuotes(taxCode)
        ]).pipe(
            take(1),
            map((res) => {
                const hasActiveRecords = res.some((totRecords) => totRecords > 0);
                if (hasActiveRecords) {
                    this.loggerSrv.warn(
                        this.translateSrv.instant('ORDER_ENTRY.ACTIVE_SUPPLIES.MSG_WARN_QUOTE_ORDER_ASSET_BB'),
                        true
                    );
                }
                return true;
            })
        );
    }
}
